import { useEffect, useState, useCallback } from "react";
import { RootState } from "../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import kernelApi from "../../API/kernelApi";
import { Logout } from "../../UtilityFunctions";
import { setHotelId } from "../../Redux/actions/LoginAction";

// TypeScript interfaces for type safety
interface Hotel {
  hotel_id: number;
  hotel_name: string;
  city_name: string;
  state_name: string;
}

const ManagePropertyDropdown = () => {
  const { company_id, auth_token, hotel_id, comp_hash } = useSelector(
    (state: RootState) => state.auth,
  );

  // State management
  const [propertyData, setPropertyData] = useState<Hotel[]>([]);
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [filteredData, setFilteredData] = useState<Hotel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const renderedData =
    !searchInput || searchInput === "" ? propertyData : filteredData;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch hotel data on mount and when dependencies change
  useEffect(() => {
    if (auth_token && company_id) {
      getHotelName(auth_token, company_id);
    }
  }, [auth_token, company_id, hotel_id]);

  const getHotelName = async (auth_token: string, company_id: string) => {
    try {
      setIsLoading(true);
      const resp = await kernelApi.get(
        `hotel_admin/get_all_hotels_by_company/${comp_hash}/${company_id}`,
        {
          headers: {
            Authorization: `Bearer ${auth_token}`,
          },
        },
      );

      // Null safety checks
      if (resp?.data?.data && Array.isArray(resp.data.data)) {
        const hotels: Hotel[] = resp.data.data;
        setPropertyData(hotels);

        // Find and set current hotel
        const currentHotel = hotels.find(
          (obj: Hotel) => obj.hotel_id === hotel_id,
        );
        if (currentHotel) {
          setHotel(currentHotel);
        }
      } else {
        console.error("Invalid API response structure");
        setPropertyData([]);
      }
    } catch (error: any) {
      console.error("Error fetching hotels:", error);
      if (error.response?.status === 401) {
        Logout();
      }
      setPropertyData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  // Memoized outside click handler to prevent memory leaks
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    const trigger = document.getElementById("manage-dropdown-trigger");
    const dropdown = document.getElementById("manage-dropdown");
    if (
      dropdown &&
      !dropdown.contains(e.target as Node) &&
      trigger &&
      !trigger.contains(e.target as Node)
    ) {
      setIsDropdownVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick]);

  // Filter logic with proper dependencies
  useEffect(() => {
    if (propertyData.length > 0) {
      const filtered = propertyData.filter(
        (each: Hotel) =>
          each.hotel_name.toLowerCase().includes(searchInput.toLowerCase()) ||
          each.city_name.toLowerCase().includes(searchInput.toLowerCase()) ||
          each.state_name.toLowerCase().includes(searchInput.toLowerCase()) ||
          each.hotel_id.toString().includes(searchInput),
      );
      setFilteredData(filtered);
    } else {
      setFilteredData([]);
    }
  }, [searchInput, propertyData]);

  // Handle property switch with navigation
  const handlePropertySwitch = async (selectedHotel: Hotel) => {
    if (isSwitching) return; // Prevent rapid clicks

    try {
      setIsSwitching(true);

      // Update Redux state
      dispatch(setHotelId(selectedHotel.hotel_id));

      // Update local state
      setHotel(selectedHotel);
      setIsDropdownVisible(false);
      setSearchInput("");

      // Navigate to dashboard to refresh data
      navigate("/");

      // Optional: Force page reload to ensure all data is refreshed
      // Uncomment if needed: window.location.reload();
    } catch (error) {
      console.error("Error switching property:", error);
      // Revert to previous hotel on error
      const previousHotel = propertyData.find((h) => h.hotel_id === hotel_id);
      if (previousHotel) {
        setHotel(previousHotel);
      }
    } finally {
      setIsSwitching(false);
    }
  };

  // Clear search input
  const handleClearSearch = () => {
    setSearchInput("");
  };

  return (
    <div className="manage_property_wrapper">
      {isLoading ?
        <div className="property_head">Loading...</div>
      : propertyData.length > 1 && hotel ?
        <>
          <div
            id="manage-dropdown-trigger"
            className="property_head"
            onClick={toggleDropdown}
          >
            {hotel.hotel_name} ({hotel.hotel_id}){" "}
            <i
              className={`dropdown_icon bi ${
                isDropdownVisible ? "bi-chevron-up" : "bi-chevron-down"
              }`}
            ></i>
          </div>
          <div
            id="manage-dropdown"
            className={`dropdown ${isDropdownVisible ? "" : "hidden"}`}
          >
            <div className="search_input_wrapper">
              <input
                type="text"
                placeholder="Search Property"
                className="search_input_box"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoFocus={isDropdownVisible}
              />
              {searchInput && (
                <i
                  className="bi bi-x-lg clear_icon"
                  onClick={handleClearSearch}
                />
              )}
            </div>
            <hr />
            <div className="property_list">
              {renderedData && renderedData.length > 0 ?
                renderedData.map((propertyItem: Hotel) => {
                  if (propertyItem.hotel_id !== hotel_id) {
                    return (
                      <div
                        key={propertyItem.hotel_id}
                        className={`dropdown_item ${isSwitching ? "disabled" : ""}`}
                        onClick={() =>
                          !isSwitching && handlePropertySwitch(propertyItem)
                        }
                      >
                        <div className="dropdown_hotel">
                          {propertyItem.hotel_name} ({propertyItem.hotel_id})
                        </div>
                        <div className="dropdown_city">
                          {propertyItem.city_name}, {propertyItem.state_name}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })
              : <div className="no_hotel_data">
                  <i className="bi bi-x-circle"></i>
                  No Data Found
                </div>
              }
            </div>
          </div>
        </>
      : hotel_id && hotel ?
        <div className="property_head">
          {hotel.hotel_name || "HotelName"} ({hotel_id})
        </div>
      : null}
    </div>
  );
};

export default ManagePropertyDropdown;
